import * as React from 'react';
import { translate } from 'components/translate-hoc';

// types
import { ITranslationProps } from 'types/translation';
import { IPlatformDisplayProps } from 'types/platforms';
import { IClickEventData } from 'types/analytics';

// constants
import { LINKABLE_PLATFORMS, PLATFORM_TYPES } from '@sitegen/common/constants/platforms';
import { PLATFORM_DISPLAY_PROPS } from 'constants/platforms-display-props';
import { PLATFORM_BUTTON_ANALYTICS_ATTRIBUTES } from './platform-selection-button.constants';

import AnalyticsService from 'modules/analytics-provider/analytics-provider.service';
import './platform-selection-button.styles.less';

export class PlatformButton extends React.Component<IPlatformButtonProps> {
    public static defaultProps: Partial<IPlatformButtonProps> = {
        disabled: false
    };

    private readonly platformDisplayProps: IPlatformDisplayProps;
    private readonly isLinkable: boolean;
    private readonly isUserLinked: boolean;

    constructor(props: IPlatformButtonProps) {
        super(props);
        this.platformDisplayProps = PLATFORM_DISPLAY_PROPS[this.props.platformId];
        this.isLinkable = LINKABLE_PLATFORMS.includes(props.platformId);
        this.isUserLinked = !!props.userName;
    }

    public clickHandler = () => {
        const { disabled, selected, onClick, platformId } = this.props;
        if (!disabled && !selected) {
            const data: IClickEventData = {
                location: PLATFORM_BUTTON_ANALYTICS_ATTRIBUTES.location,
                category: this.isLinkable
                    ? PLATFORM_BUTTON_ANALYTICS_ATTRIBUTES.category.linkapp
                    : PLATFORM_BUTTON_ANALYTICS_ATTRIBUTES.category.interaction,
                action: `select ${this.props.platformId}`
            };

            AnalyticsService.sendClickEvent(data);
            onClick(platformId);
        }
    };

    public render = (): JSX.Element | null => {
        /* TODO: Delete after ~2.7.2 is not supported. All platforms in config should have display options. */
        if (!this.platformDisplayProps) {
            return null;
        }

        return (
            <button className={this.getButtonClass()} onClick={this.clickHandler}>
                <div className="sitegen-platform-button__icon" />
                <div className="sitegen-platform-button__title">
                    {this.platformDisplayProps.name}
                </div>
                {this.renderDisabledMessage()}
                {this.getLinkStatus()}
            </button>
        );
    };

    public getLinkStatus = (): JSX.Element | null => {
        if (this.props.hideLink) {
            return null;
        }

        const { dangerouslyTranslateWithHTML: translateKey } = this.props;
        const caption: string = this.props.userName || translateKey(this.platformDisplayProps.linkAppText || '');
        const additionalClass: string = this.isUserLinked
            ? 'sitegen-platform-button__link-status--linked'
            : 'sitegen-platform-button__link-status--unlinked';

        const className: string = `sitegen-platform-button__link-status ${additionalClass}`;
        return <div className={className}>{caption}</div>;
    };

    public getButtonClass(): string {
        const { disabled, selected } = this.props;
        const { iconClass } = this.platformDisplayProps;
        const classList: string[] = ['sitegen-platform-button'];

        if (iconClass) {
            classList.push(`sitegen-icon-${iconClass}`);
        }

        if (selected) {
            classList.push('sitegen-platform-button--active');
        }

        if (disabled) {
            classList.push('sitegen-platform-button--disabled');
        }

        return classList.join(' ');
    }

    public renderDisabledMessage = (): JSX.Element | null => {
        const { disabled, disabledMessage } = this.props;
        if (disabled) {
            return (
                <div className="sitegen-platform-button__disabled-message">
                    <p>{disabledMessage}</p>
                </div>);
        }
        return null;
    };
}

interface IPlatformButtonProps extends ITranslationProps {
    readonly platformId: PLATFORM_TYPES;
    readonly userName?: string;
    readonly disabled?: boolean;
    readonly selected?: boolean;
    readonly hideLink?: boolean;
    readonly onClick: (platform: PLATFORM_TYPES) => void;
    disabledMessage?: string;
}

export default translate()(PlatformButton);
